import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Authentication state interface
 */
export interface AuthState {
  // Login flow state
  email: string;
  password: string;

  // Loading and error states
  isLoading: boolean;
  error: string | null;

  // Authentication status
  isAuthenticated: boolean;
  user: any | null;
  userProfileData: any | null;
}

/**
 * Initial state for authentication
 */
const initialState: AuthState = {
  email: "",
  password: "",
  isLoading: false,
  error: null,
  isAuthenticated: false,
  user: null,
  userProfileData: null,
};

/**
 * Auth slice for managing authentication state
 */
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    setPassword: (state, action: PayloadAction<string>) => {
      state.password = action.payload;
    },

    // Loading state management
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Error management
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Login start
    loginStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },

    // Login success
    loginSuccess: (state, action: PayloadAction<any>) => {
      state.isLoading = false;
      state.error = null;
      state.isAuthenticated = true;
      state.user = action.payload;
    },

    // Login failure
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Clear login form (reset all fields)
    clearLoginForm: (state) => {
      state.error = null;
      state.isLoading = false;
      state.email = "";
      state.password = "";
    },

    // Logout
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.error = null;
      state.isLoading = false;
    },
    // Set user profile data
    setUserProfileData: (state, action: PayloadAction<any | null>) => {
      state.userProfileData = action.payload;
    },
  },
});

// Export actions
export const {
  setEmail,
  setPassword,
  setLoading,
  setError,
  clearError,
  loginStart,
  loginSuccess,
  loginFailure,
  clearLoginForm,
  logout,
  setUserProfileData,
} = authSlice.actions;

// Export reducer
export default authSlice.reducer;
